import { createSlice } from '@reduxjs/toolkit';
import { SearchStatus } from '../../constants';

const initialState = {
  // Insurance quotes by tripId
  insuranceQuotes: {},
  // Selected insurance option by tripId
  selectedInsurance: {},
  // Loading states by tripId
  getQuotesStatus: {},
  // Error states by tripId
  error: {},
  // Quote retrieval timestamp for caching
  quotesRetrievedAt: {},
};

const insuranceSlice = createSlice({
  name: 'insurance',
  initialState,
  reducers: {
    // Get insurance quotes actions
    getInsuranceQuotesRequest: (state, { payload: { tripId } }) => ({
      ...state,
      getQuotesStatus: {
        ...state.getQuotesStatus,
        [tripId]: SearchStatus.SEARCHING,
      },
      error: {
        ...state.error,
        [tripId]: null,
      },
    }),
    getInsuranceQuotesSuccess: (state, { payload: { tripId, quotes, retrievedAt } }) => ({
      ...state,
      insuranceQuotes: {
        ...state.insuranceQuotes,
        [tripId]: quotes,
      },
      getQuotesStatus: {
        ...state.getQuotesStatus,
        [tripId]: SearchStatus.DONE,
      },
      quotesRetrievedAt: {
        ...state.quotesRetrievedAt,
        [tripId]: retrievedAt || new Date().toISOString(),
      },
      error: {
        ...state.error,
        [tripId]: null,
      },
    }),
    getInsuranceQuotesFailure: (state, { payload: { tripId, error: errorMessage } }) => ({
      ...state,
      getQuotesStatus: {
        ...state.getQuotesStatus,
        [tripId]: SearchStatus.DONE,
      },
      error: {
        ...state.error,
        [tripId]: errorMessage,
      },
    }),

    // Insurance selection actions
    selectInsuranceOption: (state, { payload: { tripId, quoteId, quote } }) => ({
      ...state,
      selectedInsurance: {
        ...state.selectedInsurance,
        [tripId]: {
          quoteId,
          quote,
          selectedAt: new Date().toISOString(),
        },
      },
    }),
    clearInsuranceSelection: (state, { payload: { tripId } }) => ({
      ...state,
      selectedInsurance: {
        ...state.selectedInsurance,
        [tripId]: null,
      },
    }),

    // Clear all insurance data for a trip
    clearInsuranceData: (state, { payload: { tripId } }) => ({
      ...state,
      insuranceQuotes: {
        ...state.insuranceQuotes,
        [tripId]: undefined,
      },
      selectedInsurance: {
        ...state.selectedInsurance,
        [tripId]: null,
      },
      getQuotesStatus: {
        ...state.getQuotesStatus,
        [tripId]: SearchStatus.INITIAL,
      },
      error: {
        ...state.error,
        [tripId]: null,
      },
      quotesRetrievedAt: {
        ...state.quotesRetrievedAt,
        [tripId]: undefined,
      },
    }),

    // Clear all insurance data (for logout, etc.)
    clearAllInsuranceData: () => initialState,
  },
});

export const {
  getInsuranceQuotesRequest,
  getInsuranceQuotesSuccess,
  getInsuranceQuotesFailure,
  selectInsuranceOption,
  clearInsuranceSelection,
  clearInsuranceData,
  clearAllInsuranceData,
} = insuranceSlice.actions;

export default insuranceSlice.reducer;
